(() => {
  if (typeof customElements === 'undefined') return;
  if (customElements.get('quas-google-reviews')) return;

  class QuasGoogleReviews extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded === 'true') return;
      this.dataset.loaded = 'true';
      this.summary = this.querySelector('[data-google-summary]');
      this.track = this.querySelector('[data-google-review-track]');
      this.viewport = this.querySelector('[data-google-review-viewport]');
      this.googleIconUrl = this.dataset.googleGIconUrl || '';
      this.googleWordmarkUrl = this.dataset.googleWordmarkUrl || '';
      this.load();
    }

    disconnectedCallback() {
      if (this.stopScroller) this.stopScroller();
    }

    async load() {
      const reviewsUrl = this.dataset.reviewsUrl;

      if (!reviewsUrl) {
        this.renderNotice('Google reviews are temporarily unavailable.');
        return;
      }

      try {
        const response = await fetch(reviewsUrl, { cache: 'force-cache' });
        if (!response.ok) throw new Error('Google reviews export unavailable.');
        const data = await response.json();
        this.renderExport(data);
      } catch (error) {
        this.renderNotice('Google reviews are temporarily unavailable.');
      }
    }

    renderExport(data) {
      this.classList.remove('is-unavailable');
      const minRating = Number(this.dataset.minRating || 1);
      const exportedReviews = Array.isArray(data.reviews) ? data.reviews : [];
      const maxRows = Number(this.dataset.maxRows || exportedReviews.length || 75);
      const reviews = exportedReviews
        .filter((review) => Number(review.rating || 0) >= minRating)
        .sort((a, b) => Number(b.time || 0) - Number(a.time || 0))
        .slice(0, maxRows);

      this.renderSummary(data.summary || {}, reviews.length);
      this.track.replaceChildren();

      if (!reviews.length) {
        this.track.appendChild(this.noticeCard(data.notice || 'No Google reviews matched the rating filter.'));
        return;
      }

      reviews.forEach((review) => this.track.appendChild(this.reviewCard(review)));
      reviews.forEach((review) => this.track.appendChild(this.reviewCard(review, true)));
      this.startScroller(Math.max(130, reviews.length * 2.9));
    }

    /**
     * The reviews drift by on their own, but the row is a real scroller: a
     * swipe, trackpad, arrow key or mouse drag takes over at once, and the
     * drift resumes a few seconds after the visitor lets go. One full set of
     * reviews passes in `loopSeconds`; the cloned set makes the wrap seamless.
     * Reduced motion keeps the row still and hides the clones.
     */
    startScroller(loopSeconds) {
      if (this.stopScroller) this.stopScroller();
      const viewport = this.viewport;
      if (!viewport) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      const RESUME_AFTER = 3000;
      const MAX_GLIDE = 2.4;
      let setWidth = 0;
      let position = viewport.scrollLeft;
      let lastSet = -1;
      let holdUntil = 0;
      let hovered = false;
      let visible = false;
      let frame = 0;
      let lastTime = 0;
      let drag = null;
      let glide = 0;
      let suppressClick = false;
      let focused = false;

      const measure = () => {
        const first = this.track.querySelector('.quas-google-review-card:not([data-review-clone])');
        const clone = this.track.querySelector('[data-review-clone]');
        setWidth = first && clone && clone.offsetParent ? clone.offsetLeft - first.offsetLeft : 0;
      };
      const hold = (ms = RESUME_AFTER) => {
        holdUntil = performance.now() + ms;
      };
      const wrap = () => {
        if (setWidth > 0 && viewport.scrollLeft >= setWidth) {
          viewport.scrollLeft -= setWidth;
          if (drag) drag.startScroll -= setWidth;
        }
        position = viewport.scrollLeft;
      };
      // Focus is tracked with events rather than a selector query every frame.
      const paused = (now) =>
        reduced.matches || !visible || hovered || drag || glide || document.hidden || now < holdUntil || focused;

      // The loop only runs while the row is on screen, so an off-screen
      // carousel costs nothing (it used to read layout on every frame).
      const tick = (now) => {
        if (!visible) {
          frame = 0;
          return;
        }
        frame = window.requestAnimationFrame(tick);
        const elapsed = lastTime ? Math.min(now - lastTime, 64) : 0;
        lastTime = now;
        // A scroll we did not make means the visitor has taken over.
        if (lastSet >= 0 && Math.abs(viewport.scrollLeft - lastSet) > 2) {
          hold();
          lastSet = -1;
          position = viewport.scrollLeft;
        }
        if (paused(now) || !setWidth) return;
        position += (setWidth / loopSeconds) * (elapsed / 1000);
        if (position >= setWidth) position -= setWidth;
        viewport.scrollLeft = position;
        lastSet = viewport.scrollLeft;
      };

      // Mouse drag with momentum. Touch and trackpads use native scrolling.
      const onPointerDown = (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        window.cancelAnimationFrame(glide);
        glide = 0;
        drag = { x: event.clientX, startScroll: viewport.scrollLeft, moved: 0, samples: [] };
        suppressClick = false;
      };
      const onPointerMove = (event) => {
        if (!drag) return;
        const dx = event.clientX - drag.x;
        if (!drag.captured && Math.abs(dx) > 4) {
          viewport.setPointerCapture(event.pointerId);
          viewport.classList.add('is-dragging');
          drag.captured = true;
        }
        if (!drag.captured) return;
        drag.moved = Math.max(drag.moved, Math.abs(dx));
        viewport.scrollLeft = Math.max(0, drag.startScroll - dx);
        wrap();
        drag.samples.push({ t: event.timeStamp, x: event.clientX });
        if (drag.samples.length > 5) drag.samples.shift();
      };
      const onPointerUp = (event) => {
        if (!drag) return;
        const { samples, captured, moved } = drag;
        drag = null;
        viewport.classList.remove('is-dragging');
        if (captured && viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        suppressClick = moved > 6;
        hold();
        if (samples.length < 2 || reduced.matches) return;
        const a = samples[0];
        const b = samples[samples.length - 1];
        // A pointer that paused before release should not glide, and a flick is
        // capped so one throw travels roughly one or two cards rather than the set.
        if (event.timeStamp - b.t > 80) return;
        const raw = b.t > a.t ? (a.x - b.x) / (b.t - a.t) : 0;
        let velocity = Math.max(-MAX_GLIDE, Math.min(MAX_GLIDE, raw));
        let prev = performance.now();
        const step = (now) => {
          const dt = Math.min(now - prev, 64);
          prev = now;
          velocity *= Math.pow(0.94, dt / 16);
          viewport.scrollLeft = Math.max(0, viewport.scrollLeft + velocity * dt);
          wrap();
          if (Math.abs(velocity) < 0.02) {
            glide = 0;
            hold();
            return;
          }
          glide = window.requestAnimationFrame(step);
        };
        glide = window.requestAnimationFrame(step);
      };
      const onClick = (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      };
      const onUserScroll = () => hold();
      const onScroll = () => {
        if (!drag && !glide) wrap();
      };
      const onEnter = () => {
        if (finePointer.matches) hovered = true;
      };
      const onLeave = () => {
        hovered = false;
      };
      const onDragStart = (event) => event.preventDefault();

      const observer = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        lastTime = 0;
        if (visible && !frame) frame = window.requestAnimationFrame(tick);
      });
      const onFocusIn = () => {
        focused = Boolean(this.querySelector(':focus-visible'));
      };
      const onFocusOut = () => {
        focused = false;
      };
      const onResize = () => {
        measure();
        wrap();
      };

      measure();
      observer.observe(viewport);
      viewport.addEventListener('pointerdown', onPointerDown);
      viewport.addEventListener('pointermove', onPointerMove);
      viewport.addEventListener('pointerup', onPointerUp);
      viewport.addEventListener('pointercancel', onPointerUp);
      viewport.addEventListener('click', onClick, true);
      viewport.addEventListener('dragstart', onDragStart);
      viewport.addEventListener('wheel', onUserScroll, { passive: true });
      viewport.addEventListener('touchstart', onUserScroll, { passive: true });
      viewport.addEventListener('keydown', onUserScroll);
      viewport.addEventListener('scroll', onScroll, { passive: true });
      viewport.addEventListener('pointerenter', onEnter);
      viewport.addEventListener('pointerleave', onLeave);
      window.addEventListener('resize', onResize);
      this.addEventListener('focusin', onFocusIn);
      this.addEventListener('focusout', onFocusOut);

      this.stopScroller = () => {
        window.cancelAnimationFrame(frame);
        window.cancelAnimationFrame(glide);
        observer.disconnect();
        viewport.removeEventListener('pointerdown', onPointerDown);
        viewport.removeEventListener('pointermove', onPointerMove);
        viewport.removeEventListener('pointerup', onPointerUp);
        viewport.removeEventListener('pointercancel', onPointerUp);
        viewport.removeEventListener('click', onClick, true);
        viewport.removeEventListener('dragstart', onDragStart);
        viewport.removeEventListener('wheel', onUserScroll);
        viewport.removeEventListener('touchstart', onUserScroll);
        viewport.removeEventListener('keydown', onUserScroll);
        viewport.removeEventListener('scroll', onScroll);
        viewport.removeEventListener('pointerenter', onEnter);
        viewport.removeEventListener('pointerleave', onLeave);
        window.removeEventListener('resize', onResize);
        this.removeEventListener('focusin', onFocusIn);
        this.removeEventListener('focusout', onFocusOut);
        this.stopScroller = null;
      };
    }

    renderSummary(summaryData, shownCount) {
      const rating = Number(summaryData.rating || 0);
      const totalCount = Number(summaryData.count || shownCount || 0);
      const textCount = Number(summaryData.text_review_count || 0);
      const ratingOnlyCount = Number(summaryData.rating_only_count || 0);
      const url = summaryData.url || 'https://www.google.com/search?q=Quas+Drinks+reviews';
      const summary = document.createElement('a');
      summary.className = 'quas-google-reviews__card';
      summary.href = url;
      summary.target = '_blank';
      summary.rel = 'noopener noreferrer';

      // One aligned row: mark, score, then stars over the total they come from.
      const main = document.createElement('span');
      main.className = 'quas-google-reviews__card-main';

      const score = document.createElement('span');
      score.className = 'quas-google-reviews__card-score';
      score.textContent = rating.toFixed(1);

      const ratingStack = document.createElement('span');
      ratingStack.className = 'quas-google-reviews__card-rating';

      const stars = document.createElement('span');
      stars.className = 'quas-google-reviews__card-stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
      stars.textContent = this.stars(rating);

      const total = document.createElement('span');
      total.className = 'quas-google-reviews__card-total';
      total.textContent = `${totalCount} Google reviews`;

      ratingStack.append(stars, total);
      main.append(this.googleIcon('quas-google-reviews__card-mark'), score, ratingStack);

      const foot = document.createElement('span');
      foot.className = 'quas-google-reviews__card-foot';

      const split = document.createElement('span');
      split.className = 'quas-google-reviews__card-split';
      split.textContent = ratingOnlyCount
        ? `${textCount} written \u00b7 ${ratingOnlyCount} rating only`
        : `${shownCount} written reviews`;

      const cta = document.createElement('span');
      cta.className = 'quas-google-reviews__card-cta';
      cta.innerHTML = 'Read on Google<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M5 11 11 5M6 5h5v5"/></svg>';

      foot.append(split, cta);
      summary.append(main, foot);
      this.summary.replaceChildren(summary);
    }

    reviewCard(review, isClone = false) {
      const card = document.createElement('div');
      card.className = 'quas-google-review-card';
      if (review.rating_only) {
        card.classList.add('quas-google-review-card--rating-only');
      }
      card.setAttribute('role', 'listitem');
      if (isClone) {
        card.dataset.reviewClone = 'true';
        card.setAttribute('aria-hidden', 'true');
      }

      const stars = document.createElement('div');
      stars.className = 'quas-google-review-card__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.rating || 0} out of 5 stars`);
      stars.textContent = this.stars(review.rating || 0);

      const source = document.createElement('div');
      source.className = 'quas-google-review-card__source';
      source.appendChild(this.googleIcon());
      source.appendChild(document.createTextNode(review.rating_only ? 'Rating only' : 'Google'));

      const meta = document.createElement('div');
      meta.className = 'quas-google-review-card__meta';
      meta.append(stars, source);

      const text = document.createElement('p');
      text.className = 'quas-google-review-card__text';
      text.tabIndex = isClone ? -1 : 0;
      text.setAttribute('role', 'region');
      text.setAttribute('aria-label', `Review by ${review.author_name || 'a Quas customer'}`);
      text.textContent = this.trim(review.display_text || review.text || '', review.rating_only ? 120 : 260);

      const author = document.createElement('div');
      author.className = 'quas-google-review-card__author';

      const avatar = review.profile_photo_url
        ? this.profilePhoto(review.profile_photo_url, review.author_name)
        : this.avatarFallback(review.author_name);

      const details = document.createElement('div');
      const name = document.createElement('a');
      name.className = 'quas-google-review-card__name';
      name.href = review.author_url || 'https://www.google.com/search?q=Quas+Drinks+reviews';
      name.target = '_blank';
      name.rel = 'noopener noreferrer';
      if (isClone) name.tabIndex = -1;
      name.textContent = this.shortName(review.author_name);

      const time = document.createElement('div');
      time.className = 'quas-google-review-card__time';
      time.textContent = review.relative_time_description || '';

      details.append(name, time);
      author.append(avatar, details);
      card.append(meta, text, author);
      return card;
    }

    renderNotice(message) {
      this.classList.add('is-unavailable');
      const url = 'https://www.google.com/search?q=Quas+Drinks+reviews';
      this.summary.replaceChildren(this.summaryLink('Google', 'reviews', 'Open Google reviews', url));
      this.track.replaceChildren(this.noticeCard(message));
    }

    summaryLink(scoreText, starsText, countText, url) {
      const summary = document.createElement('a');
      summary.className = 'quas-google-reviews__summary-link';
      summary.href = url;
      summary.target = '_blank';
      summary.rel = 'noopener noreferrer';

      const mark = this.googleIcon('quas-google-reviews__summary-mark');
      const meta = document.createElement('span');
      meta.className = 'quas-google-reviews__summary-meta';

      const score = document.createElement('span');
      score.className = 'quas-google-reviews__score';
      score.textContent = scoreText;

      const stars = document.createElement('span');
      stars.className = 'quas-google-reviews__stars';
      stars.textContent = starsText;

      const count = document.createElement('span');
      count.className = 'quas-google-reviews__count';
      count.textContent = countText;

      meta.append(score, stars, count);
      summary.append(mark, meta);
      return summary;
    }

    noticeCard(message) {
      const card = document.createElement('div');
      card.className = 'quas-google-review-card quas-google-review-card--notice';
      card.setAttribute('role', 'listitem');

      const source = document.createElement('div');
      source.className = 'quas-google-review-card__source';
      source.appendChild(this.googleIcon());
      source.appendChild(document.createTextNode('Google Business Profile feed'));

      const text = document.createElement('p');
      text.className = 'quas-google-review-card__text';
      text.textContent = message;

      card.append(source, text);
      return card;
    }

    stars(rating) {
      const rounded = Math.max(0, Math.min(5, Math.round(Number(rating || 0))));
      return '\u2605'.repeat(rounded) + '\u2606'.repeat(5 - rounded);
    }

    googleIcon(className = 'quas-google-reviews__gmark') {
      const icon = document.createElement('img');
      icon.className = className;
      icon.src = this.googleIconUrl;
      icon.alt = '';
      icon.width = className.includes('summary') ? 58 : 24;
      icon.height = className.includes('summary') ? 58 : 24;
      icon.loading = 'lazy';
      icon.decoding = 'async';
      return icon;
    }

    googleWordmark() {
      const wordmark = document.createElement('img');
      wordmark.src = this.googleWordmarkUrl;
      wordmark.alt = 'Google';
      wordmark.width = 74;
      wordmark.height = 25;
      wordmark.loading = 'lazy';
      wordmark.decoding = 'async';
      return wordmark;
    }

    profilePhoto(src, name) {
      const photo = document.createElement('img');
      photo.className = 'quas-google-review-card__avatar';
      photo.src = src;
      photo.alt = '';
      photo.width = 40;
      photo.height = 40;
      photo.loading = 'lazy';
      photo.decoding = 'async';
      photo.referrerPolicy = 'no-referrer';
      photo.addEventListener('error', () => photo.replaceWith(this.avatarFallback(name)), { once: true });
      return photo;
    }

    avatarFallback(name) {
      const avatar = document.createElement('span');
      avatar.className = 'quas-google-review-card__avatar';
      avatar.setAttribute('aria-hidden', 'true');
      avatar.textContent = this.initials(name);
      return avatar;
    }

    initials(name = 'Google reviewer') {
      return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0])
        .join('')
        .toUpperCase();
    }

    shortName(name = 'Google reviewer') {
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length < 2) return name;
      return `${parts[0]} ${parts[1][0]}.`;
    }

    trim(text, limit) {
      if (text.length <= limit) return text;
      return `${text.slice(0, limit - 3).trim()}...`;
    }

    renderError(message) {
      this.renderNotice(message);
    }
  }

  customElements.define('quas-google-reviews', QuasGoogleReviews);
})();
